/**
 * Meus tickets: lista dos tickets dos clientes do usuário
 * (GET /portal/tickets — status/search/limit/offset, total no X-Total-Items),
 * filtro Abertos/Fechados/Todos, busca e paginação simples.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { TicketStatusChip } from '@/features/tickets/detail-status-chip';
import { useDebouncedValue } from '@/features/tickets/use-debounced-value';
import { cn, formatDateTime } from '@/lib/utils';
import { useIsImpersonating } from '@/auth/impersonation';
import { errorMessage, portalTicketsQuery } from './portal-queries';
import { PortalLink, portalPath } from './portal-nav';

const PAGE_SIZE = 20;

type StatusFilter = 'open' | 'closed' | 'all';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'open', label: 'Abertos' },
  { value: 'closed', label: 'Fechados' },
  { value: 'all', label: 'Todos' },
];

export function PortalTickets() {
  const [status, setStatus] = useState<StatusFilter>('open');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const impersonating = useIsImpersonating();

  const list = useQuery(
    portalTicketsQuery({
      status,
      search: debouncedSearch || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    })
  );

  const total = list.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const changeStatus = (next: StatusFilter) => {
    setStatus(next);
    setPage(0);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-foreground">Meus tickets</h1>
        {!impersonating && (
          <Button asChild>
            <PortalLink to={portalPath.newTicket}>
              <Plus className="size-4" strokeWidth={2.5} aria-hidden />
              Abrir novo ticket
            </PortalLink>
          </Button>
        )}
      </div>

      {/* Filtros: status segmentado + busca */}
      <div className="flex flex-wrap items-center gap-3">
        <div
          role="group"
          aria-label="Filtrar por status"
          className="flex h-9 items-center rounded-md border bg-card p-0.5"
        >
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => changeStatus(opt.value)}
              aria-pressed={status === opt.value}
              className={cn(
                'h-8 rounded-[5px] px-3 text-sm font-medium transition-colors',
                status === opt.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="relative w-full max-w-xs">
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Buscar por título ou número"
            aria-label="Buscar tickets"
            className="pr-8"
          />
          <Search
            className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
        {list.isPending ? (
          <div aria-busy className="divide-y divide-divider">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="space-y-2 p-4">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-48" />
              </div>
            ))}
          </div>
        ) : list.isError ? (
          <EmptyState message={`Erro ao carregar os tickets: ${errorMessage(list.error)}`} />
        ) : list.data.items.length === 0 ? (
          <EmptyState message="Nenhum ticket encontrado." />
        ) : (
          <ul className="divide-y divide-divider">
            {list.data.items.map((t) => (
              <li key={t.ticketNumber}>
                <PortalLink
                  to={portalPath.ticket(t.ticketNumber)}
                  className="flex items-center gap-3 p-4 transition-colors hover:bg-accent/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 text-sm font-semibold text-primary tabular-nums">
                        #{t.ticketNumber}
                      </span>
                      <span className="truncate text-sm font-medium text-foreground">
                        {t.title}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-[13px] text-muted-foreground">
                      <span className="truncate">{t.deskName}</span>
                      <span aria-hidden>·</span>
                      <span className="tabular-nums">Criado em {formatDateTime(t.createdAt)}</span>
                    </div>
                  </div>
                  <TicketStatusChip kind={t.statusKind} name={t.statusName} />
                </PortalLink>
              </li>
            ))}
          </ul>
        )}

        {/* Paginação simples */}
        {!list.isPending && !list.isError && total > PAGE_SIZE ? (
          <div className="flex items-center justify-between border-t px-4 py-2.5">
            <span className="text-[13px] text-muted-foreground tabular-nums">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="size-4" aria-hidden />
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page + 1 >= pageCount}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
                <ChevronRight className="size-4" aria-hidden />
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
